import { GridLayout } from "@tento-ui/components/grid/grid-layout";
import { StdCharter } from "@tento-ui/std-charter";
import { StdCharterGen } from "@tento-ui/std-charter.gen";
import type { Storybook } from "@tento-ui/ui/storybook";
import { CoreChatCodexTurnStoryBook } from "@tento-chat/chat/looks/codex-turn.core.example";
import { SessionReplayStoryBook } from "@tento-chat/term/session-replay.example";
import { StorybookContainer } from "./storybook-container";

// The catalogue's Core sections, derived from the std- charter: the eleven
// categories in `StdCharter.CATEGORIES` joined to the generated
// `StdCharterGen.STORIES` by primary tag, so a card can only appear in a
// section the allowlist placed it in. Allowlisted tags with no story render as
// gaps, so a hole in the library is visible rather than silent.
export namespace CoreRegistry {
  type Section = StorybookContainer.Section;

  /** Per-category grid overrides — presentation, so not in the allowlist. */
  const CORE_MIN_WIDTH: Partial<Record<StdCharter.Category, number>> = {
    "table-list": 500,
    layout: 500,
  };

  /** Core stories for allowlisted tags whose components live in the chat
   *  package. The generator only walks tento-ui-js, and tento-ui-js may not
   *  import `@tento-chat`, so the catalogue — which imports both — adds them. */
  export const ADJACENT: readonly { make: () => Storybook; tags: string[] }[] = [
    { make: () => new CoreChatCodexTurnStoryBook(), tags: ["std-chat-codex-turn"] },
    { make: () => new SessionReplayStoryBook(), tags: ["std-session-replay"] },
  ];

  export function sections(): Section[] {
    const categoryOf = new Map(
      StdCharterGen.TAGS.map((t) => [t.tag, t.category] as const),
    );
    const stories = [...StdCharterGen.STORIES, ...ADJACENT];
    const covered = new Set(stories.flatMap((s) => s.tags));

    const core: Section[] = StdCharter.CATEGORIES.map(({ id, label }) => ({
      id,
      label,
      gridConfig: {
        ...GridLayout.DEFAULT_CONFIG,
        minWidthPx: CORE_MIN_WIDTH[id] ?? 400,
      },
      items: stories.filter(
        (s) => categoryOf.get(s.tags[0]) === id,
      ).map((s) => s.make()),
      gaps: StdCharterGen.TAGS.filter(
        (t) => t.category === id && !covered.has(t.tag),
      ).map((t) => t.tag),
    }));

    return core;
  }
}
